/**
 * CP-80: Departure Port filter options — city names, collapse near-duplicates,
 * drop junk itinerary labels, keep Unknown/Cruising out of the shop list.
 *
 * Does not invent ports. Codes map to city names only when known from existing
 * embark codes already present alongside city strings in catalog data.
 * Filter matching expands a selected key back to every raw value in the group
 * (see rawValues on each option, and backend departure_port_normalize).
 */

/** Known embark / IATA-style codes → city (no invented geography). */
export const PORT_CODE_TO_CITY = {
  ADL: 'Adelaide',
  AKL: 'Auckland',
  ATH: 'Athens',
  BCN: 'Barcelona',
  BGI: 'Bridgetown',
  BNE: 'Brisbane',
  BOS: 'Boston',
  BUE: 'Buenos Aires',
  CPH: 'Copenhagen',
  CPT: 'Cape Town',
  FLL: 'Fort Lauderdale',
  HEL: 'Helsinki',
  HKG: 'Hong Kong',
  HNL: 'Honolulu',
  IST: 'Istanbul',
  LAX: 'Los Angeles',
  MEL: 'Melbourne',
  MIA: 'Miami',
  NAP: 'Naples',
  NYC: 'New York',
  REK: 'Reykjavik',
  ROM: 'Rome',
  SAN: 'San Diego',
  SEA: 'Seattle',
  SFO: 'San Francisco',
  SIN: 'Singapore',
  SJU: 'San Juan',
  SOU: 'Southampton',
  SYD: 'Sydney',
  TRS: 'Trieste',
  TYO: 'Tokyo',
  YOK: 'Yokohama',
  YQB: 'Quebec City',
  YVR: 'Vancouver'
};

const JUNK_RE = /\bending(?:\s+in)?\b|\bwith\s+\d+\s+stops?\b/i;
const HONEST_NON_PORT = new Set(['unknown', 'cruising']);

function foldAscii(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function collapseWs(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

/** True when the string is an itinerary description, not a port. */
export function isJunkDeparturePort(raw) {
  const s = collapseWs(raw);
  if (!s) return true;
  if (s.toUpperCase() === 'MISSING PORT CONTENT') return true;
  return JUNK_RE.test(s);
}

/** Unknown / Cruising — omit from the shop filter (honest non-ports). */
export function isHonestNonPort(raw) {
  return HONEST_NON_PORT.has(foldAscii(collapseWs(raw)));
}

export function isPortCode(raw) {
  // Embark codes in catalog data are ALL CAPS (FLL, SEA, WH1). Do not treat
  // short title-case city names (Bari, Oslo, Kiel) as codes.
  const s = collapseWs(raw);
  return /^[A-Z0-9]{2,4}$/.test(s);
}

/**
 * City head for collapse: drop ", State/Country…" tails so
 * "Fort Lauderdale, FL, USA" and "Ft. Lauderdale, Florida" share a key.
 * Does not invent a different city — only truncates after the first comma.
 */
function stripRegionSuffixes(value) {
  const s = collapseWs(value);
  if (!s) return s;
  const comma = s.indexOf(',');
  if (comma === -1) return s;
  return collapseWs(s.slice(0, comma));
}

function normalizeCityHead(value) {
  let s = stripRegionSuffixes(value);
  s = foldAscii(s);
  s = s.replace(/\bft\.?\b/g, 'fort');
  // Facility / "for X" parentheticals that do not change the embark city.
  s = s.replace(/\(\s*port everglades\s*\)/g, ' ');
  s = s.replace(/\(\s*tours to [^)]+\)/g, ' ');
  s = s.replace(/\(\s*for [^)]+\)/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  // If still "city (alias)", keep both tokens in the key so distinct facilities
  // like Barcelona (Tarragona) do not silently merge with Barcelona.
  s = s.replace(/[^\w\s()]/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

/** Stable grouping key for a raw departure_port string. Empty ⇒ drop. */
export function departurePortKey(raw) {
  const s = collapseWs(raw);
  if (!s || isJunkDeparturePort(s) || isHonestNonPort(s)) return '';

  if (isPortCode(s)) {
    const upper = s.toUpperCase();
    const city = PORT_CODE_TO_CITY[upper];
    if (city) return departurePortKey(city);
    return foldAscii(upper);
  }

  const head = normalizeCityHead(s);
  if (!head) return '';

  // Alias bridges that already appear as alternate strings in catalog data.
  const aliases = {
    'port everglades': 'fort lauderdale',
    'ft lauderdale': 'fort lauderdale',
    'cape liberty new york': 'cape liberty',
    'cape liberty bayonne': 'cape liberty',
    'rome civitavecchia': 'rome',
    'civitavecchia rome': 'rome',
    'civitavecchia': 'rome',
    'athens piraeus': 'athens',
    'piraeus athens': 'athens',
    'piraeus': 'athens',
    'london southampton': 'southampton',
    'southampton london': 'southampton',
    'quebec qc': 'quebec city',
    quebec: 'quebec city',
    'quebec city canada': 'quebec city',
    'new york city': 'new york',
    'new york city manhattan or brooklyn': 'new york',
    'new york ny': 'new york',
    'amsterdam the netherlands': 'amsterdam',
    'amsterdam netherlands': 'amsterdam',
    'rotterdam amsterdam': 'rotterdam',
    'rotterdam the netherlands': 'rotterdam',
    "honolulu hawai'i": 'honolulu',
    'honolulu hawaii': 'honolulu',
    'honolulu oahu': 'honolulu',
    'honolulu oahu hawaii': 'honolulu',
    'whittier alaska': 'whittier',
    'anchorage whittier': 'whittier',
    'anchorage whittier alaska': 'whittier',
    'seoul incheon': 'incheon',
    'san antonio for santiago': 'san antonio',
    'san antonio santiago': 'san antonio',
    'san antonio tours to santiago': 'san antonio',
    'santiago san antonio': 'san antonio',
    'santiago valparaiso': 'valparaiso',
    'valparaiso santiago': 'valparaiso',
    'port canaveral orlando': 'port canaveral',
    'venice chioggia': 'venice',
    'venice fusina': 'venice',
    'venice ravenna': 'venice',
    'venice trieste': 'venice',
    'venice marghera': 'venice',
    'ravenna venice': 'venice',
    'trieste for venice': 'trieste',
    'trieste venice': 'trieste',
    'benoa bali': 'benoa',
    'bali benoa': 'benoa',
    'bangkok laem chabang': 'bangkok',
    'tokyo yokohama': 'tokyo',
    'yokohama tours to tokyo': 'yokohama'
  };
  // Normalize paren form "rome (civitavecchia)" → "rome civitavecchia" for alias table.
  const flat = head.replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  const aliased = aliases[flat] || flat;
  return aliased.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function titleCaseCity(name) {
  const s = collapseWs(name);
  if (!s) return '';
  // Keep small words tidy; port names are mostly Proper Nouns.
  return s
    .split(' ')
    .map((w) => {
      if (!w) return w;
      if (/^[A-Z]{2,4}$/.test(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

/**
 * Consumer label for a collapsed group. Prefer a short city name already
 * supported by data (or the known code→city map). Never invent a new city.
 */
export function departurePortLabel(rawValues, key) {
  const values = (Array.isArray(rawValues) ? rawValues : []).map(collapseWs).filter(Boolean);
  if (!values.length) {
    if (key && PORT_CODE_TO_CITY[key.toUpperCase()]) {
      return PORT_CODE_TO_CITY[key.toUpperCase()];
    }
    return key ? titleCaseCity(key.replace(/_/g, ' ')) : '';
  }

  // Known code in the group → use its city.
  for (const raw of values) {
    if (isPortCode(raw)) {
      const city = PORT_CODE_TO_CITY[raw.toUpperCase()];
      if (city) return city;
    }
  }

  // Prefer a short, non-parenthetical, non-suffixed city string from the group.
  const scored = values.map((raw) => {
    let base = stripRegionSuffixes(raw);
    base = base.replace(/\(\s*port everglades\s*\)/gi, '').replace(/\s+/g, ' ').trim();
    base = base.replace(/\(\s*tours to [^)]+\)/gi, '').replace(/\s+/g, ' ').trim();
    // Drop trailing ", Florida"-style if strip missed (already stripped).
    const hasParen = /\(/.test(base);
    const commaCount = (base.match(/,/g) || []).length;
    const isCode = isPortCode(base);
    let score = 0;
    if (isCode) score -= 50;
    if (hasParen) score -= 10;
    score -= commaCount * 5;
    score -= base.length;
    // Prefer ASCII Reykjavik spelling when both exist.
    if ([...base].some((ch) => ch.charCodeAt(0) > 127)) score -= 3;
    return { raw, base: collapseWs(base), score };
  });
  scored.sort((a, b) => b.score - a.score || a.base.localeCompare(b.base));
  const best = scored[0].base || scored[0].raw;
  // If best is still a bare code, show the code honestly.
  if (isPortCode(best)) return best.toUpperCase();
  // Normalize Ft. → Fort for display consistency.
  return collapseWs(best.replace(/\bFt\.?\b/g, 'Fort'));
}

/**
 * Collapse raw /filters/all departure_ports into shop options.
 * @returns {{ key: string, label: string, rawValues: string[] }[]}
 */
export function normalizeDeparturePortOptions(list) {
  if (!Array.isArray(list)) return [];
  const byKey = new Map();
  for (const item of list) {
    if (item == null) continue;
    const raw = typeof item === 'string' ? item : String(item);
    const trimmed = collapseWs(raw);
    if (!trimmed) continue;
    if (isJunkDeparturePort(trimmed) || isHonestNonPort(trimmed)) continue;
    const key = departurePortKey(trimmed);
    if (!key) continue;
    let group = byKey.get(key);
    if (!group) {
      group = { key, rawValues: [] };
      byKey.set(key, group);
    }
    if (!group.rawValues.includes(trimmed)) group.rawValues.push(trimmed);
  }

  const out = [];
  for (const group of byKey.values()) {
    group.rawValues.sort((a, b) => a.localeCompare(b));
    out.push({
      key: group.key,
      label: departurePortLabel(group.rawValues, group.key),
      rawValues: group.rawValues
    });
  }
  out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return out;
}

/** Label for chips / summary given a selected key (or legacy raw). */
export function labelForDeparturePort(options, selected) {
  if (selected == null || selected === '') return '';
  const key = String(selected);
  if (!Array.isArray(options)) return key;
  for (const opt of options) {
    if (!opt) continue;
    if (opt.key === key) return opt.label || key;
    if (Array.isArray(opt.rawValues) && opt.rawValues.includes(key)) return opt.label || key;
    if (departurePortKey(key) && opt.key === departurePortKey(key)) return opt.label || key;
  }
  // Legacy raw / unknown: show cleaned string, not a fabricated city.
  if (isPortCode(key) && PORT_CODE_TO_CITY[key.toUpperCase()]) {
    return PORT_CODE_TO_CITY[key.toUpperCase()];
  }
  return collapseWs(key);
}

/**
 * Resolve a URL/legacy raw value or key onto a canonical option key.
 * Returns '' when nothing matches (caller may clear the filter).
 */
export function resolveDeparturePortSelection(options, selected) {
  if (selected == null || selected === '') return '';
  const value = String(selected);
  if (!Array.isArray(options) || options.length === 0) {
    // Options not loaded yet — keep the value so hydrate survives the fetch.
    return value;
  }
  for (const opt of options) {
    if (!opt) continue;
    if (opt.key === value) return opt.key;
    if (Array.isArray(opt.rawValues) && opt.rawValues.includes(value)) return opt.key;
  }
  const collapsed = departurePortKey(value);
  if (collapsed) {
    for (const opt of options) {
      if (opt && opt.key === collapsed) return opt.key;
    }
  }
  return '';
}

/** Whether `selected` (key or raw) is still valid for the current options. */
export function departurePortSelectionIsValid(options, selected) {
  if (!selected) return true;
  return resolveDeparturePortSelection(options, selected) !== '';
}
